// features/cx/payments/hooks.ts

import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { toast } from "@shared/hooks/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { downloadCSV } from "@shared/utils/utils";
import { paymentsApi } from "./api";

// ================================
// CONSUMER SEARCH HOOKS
// ================================

export const useConsumerSearch = (filters: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.consumerSearch(filters),
    () => paymentsApi.searchConsumer(filters),
    {
      enabled: filters?.searchData?.length >= 3,
      staleTime: 5 * 60 * 1000,
      retry: false,
    },
  );
};

export const useConsumerMetaData = (params: {
  consumerId: number;
  remoteUtilityId: number;
  enabled: boolean;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.consumerMetaData(
      params.consumerId,
      params.remoteUtilityId,
    ),
    () => paymentsApi.getConsumerMetaData(params),
    {
      enabled: params.enabled && !!params.consumerId,
    },
  );
};

// ================================
// SUMMARY HOOKS
// ================================

export const usePaymentManagementSummary = (remoteUtilityId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.summary(remoteUtilityId),
    () => paymentsApi.getPaymentManagementSummary(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
    },
  );
};

export const useCheques = (filters: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.cheques(filters),
    () => paymentsApi.getCheques(filters),
    {
      enabled: !!filters?.remoteUtilityId,
    },
  );
};

export const useChequesKpi = (remoteUtilityId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.chequesKpi(remoteUtilityId),
    () => paymentsApi.getChequesKpi(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
    },
  );
};

export const useChequeDetails = (chequeId: number, remoteUtilityId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.chequeDetails(chequeId, remoteUtilityId),
    () => paymentsApi.getChequeDetails(chequeId, remoteUtilityId),
    {
      enabled: !!chequeId && !!remoteUtilityId,
    },
  );
};

export const useCreateBank = () => {
  return useSmartMutation((payload: any) => paymentsApi.createBank(payload), {
    onSuccess: (_data, variables) => {
      toast({
        title: "Bank added successfully!",
        variant: "default",
      });
      queryClient.refetchQueries({
        queryKey: ["cx", "payment", "banks"],
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to add bank",
        description: error?.response?.data?.message || "An error occurred",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteBank = () => {
  return useSmartMutation(
    ({
      bankId,
      remoteUtilityId,
    }: {
      bankId: number;
      remoteUtilityId: number;
    }) => paymentsApi.deleteBank(bankId, remoteUtilityId),
    {
      onSuccess: () => {
        toast({
          title: "Bank deleted successfully!",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "payment", "banks"],
        });
      },
      onError: (error: any) => {
        toast({
          title: "Failed to delete bank",
          description: error?.response?.data?.message || "An error occurred",
          variant: "destructive",
        });
      },
    },
  );
};

export const useBanks = (remoteUtilityId: number, enabled: boolean) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.banks(remoteUtilityId),
    () => paymentsApi.getBanks(remoteUtilityId),
    {
      enabled: !!remoteUtilityId && enabled,
    },
  );
};

export const useMarkChequeDishonoured = (remoteUtilityId: number) => {
  return useSmartMutation(
    ({ chequeId, payload }: { chequeId: number; payload: any }) =>
      paymentsApi.markChequeDishonoured(chequeId, payload),
    {
      onSuccess: () => {
        toast({
          title: "Cheque marked as dishonoured successfully!",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "payment", "chequeDetails"],
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "payment", "cheques"],
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "payment", "chequesKpi"],
        });
      },
      onError: (error: any) => {
        toast({
          title: `Failed to mark cheque as dishonoured: ${error.message}`,
          variant: "destructive",
        });
      },
    },
  );
};

export const useUpdateChequeStatus = () => {
  return useSmartMutation(
    ({
      chequeId,
      action,
      payload,
    }: {
      chequeId: number;
      action: "mark-deposited" | "mark-cleared";
      payload: { remote_utility_id: number };
    }) => paymentsApi.updateChequeStatus(chequeId, action, payload),
    {
      onSuccess: () => {
        toast({
          title: "Cheque status updated successfully!",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "payment", "chequeDetails"],
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "payment", "cheques"],
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "payment", "chequesKpi"],
        });
      },
      onError: (error: any) => {
        toast({
          title: `Failed to update cheque status: ${error.message}`,
          variant: "destructive",
        });
      },
    },
  );
};

export const useUserNames = (remoteUtilityId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.userNames(remoteUtilityId),
    () => paymentsApi.getUserNames(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
    },
  );
};

export const useCreditNoteSummary = (remoteUtilityId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.creditNoteSummary(remoteUtilityId),
    () => paymentsApi.getCreditNoteSummary(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
    },
  );
};

// ================================
// CONSUMER BILL HOOKS
// ================================

export const usePendingBills = (params: {
  remoteUtilityId: number;
  consumerId: number;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.pendingBills(
      params.remoteUtilityId,
      params.consumerId,
    ),
    () => paymentsApi.getPendingConsumerBill(params),
    {
      enabled: !!params.remoteUtilityId && !!params.consumerId,
    },
  );
};

export const usePaymentServices = (params: {
  remoteUtilityId: number;
  consumerId: number;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.services(params),
    () => paymentsApi.getPaymentServices(params),
    {
      enabled: !!params.remoteUtilityId && !!params.consumerId,
      staleTime: 5 * 60 * 1000,
    },
  );
};

// ================================
// PAYMENT HOOKS
// ================================

export const usePayments = (filters: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.list(filters),
    () => paymentsApi.getPayments(filters),
    {
      enabled: !!filters?.remoteUtilityId,
      staleTime: 2 * 60 * 1000,
    },
  );
};

export const useServiceOrder = (consumerId: any) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.serviceOrderData({
      remote_utility_id: remoteUtilityId,
      consumer_id: consumerId,
    }),
    () => paymentsApi.getServiceOrder(remoteUtilityId, consumerId),
    {
      enabled: !!consumerId && !!remoteUtilityId,
    },
  );
};

export const usePaymentExport = (filters: any) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportQuery = useSmartQuery(
    QueryKeyFactory.module.cx.payments.exportList(filters),
    async () => {
      setIsExporting(true);
      try {
        const res = await paymentsApi.exportPaymentsList(filters);
        toast({
          title: "Export Initiated",
          description: res?.message,
        });
        return res;
      } catch (error: any) {
        toast({
          title: `Failed to export payments: ${error.message}`,
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsExporting(false);
      }
    },
    { enabled: false },
  );

  return {
    exportCSV: () => exportQuery.refetch(),
    isExporting: isExporting || exportQuery.isFetching,
    error: exportQuery.error,
    isError: exportQuery.isError,
  };
};

export const usePaymentDetail = (paymentId: number) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.details(paymentId),
    () => paymentsApi.getPaymentDetail({ paymentId, remoteUtilityId }),
    {
      enabled: !!paymentId && !!remoteUtilityId,
    },
  );
};

export const usePaymentSingleBillDetail = (paymentId: number) => {
  const remoteUtilityId = useRemoteUtilityId();
  // Using a distinct query key suffix to prevent clashing with standard details
  return useSmartQuery(
    [...QueryKeyFactory.module.cx.payments.details(paymentId), "singleBill"],
    () =>
      paymentsApi.getPaymentSingleBillDetail({
        paymentId,
        remoteUtilityId: Number(remoteUtilityId),
      }),
    {
      enabled: !!paymentId && !!remoteUtilityId,
    },
  );
};

export const usePaymentBillAllocations = (
  paymentId: number,
  enabled: boolean = true,
) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    [
      ...QueryKeyFactory.module.cx.payments.details(paymentId),
      "billAllocations",
    ],
    () =>
      paymentsApi.getPaymentBillAllocations({
        paymentId,
        remoteUtilityId: Number(remoteUtilityId),
      }),
    {
      enabled: !!paymentId && !!remoteUtilityId && enabled,
    },
  );
};

export const useCreditNoteDetail = (creditNoteId: number) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.creditNoteDetails(creditNoteId),
    () => paymentsApi.getCreditNoteDetail(creditNoteId, remoteUtilityId),
    {
      enabled: !!creditNoteId && !!remoteUtilityId,
    },
  );
};

export const useCreatePayment = (
  remoteUtilityId: number,
  consumer: number,
  consumerNo: string,
) => {
  const navigate = useNavigate();

  return useSmartMutation(
    (payload: any) => paymentsApi.createPayment(payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.payments.list(),
        QueryKeyFactory.module.cx.payments.summary(remoteUtilityId),
        QueryKeyFactory.module.cx.payments.latestBill(
          remoteUtilityId,
          consumer,
          consumerNo,
        ),
      ],
      onSuccess: (data, variables) => {
        toast({ title: "Payment Created" });

        queryClient.refetchQueries({
          predicate: (q) =>
            Array.isArray(q.queryKey) &&
            q.queryKey[0] === "cxonboarding" &&
            q.queryKey[1] === "consumerList",
        });

        queryClient.refetchQueries({
          queryKey: ["cx", "payment", "list"],
        });
         queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.serviceRequests
            .list()
            .slice(0, 3),
          exact: false,
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "payment", "summary"],
        });
      },
    },
  );
};

export const useCreateNscPayment = (
  remoteUtilityId: number,
  consumer: number,
  consumerNo: string,
) => {
  const navigate = useNavigate();

  return useSmartMutation(
    (payload: any) => paymentsApi.createPayment(payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.payments.list(),
        QueryKeyFactory.module.cx.payments.summary(remoteUtilityId),
        QueryKeyFactory.module.cx.payments.latestBill(
          remoteUtilityId,
          consumer,
          consumerNo,
        ),
      ],
      onSuccess: (data, variables) => {
        navigate("/cx/payments/success#nsc", {
          state: { ...data, formData: variables },
        });

        toast({ title: "Payment Created" });
      },
    },
  );
};

export const useCreatePlanPayment = (
  remoteUtilityId: number,
  consumer: number,
  consumerNo: string,
) => {
  return useSmartMutation(
    (payload: any) => paymentsApi.createPlanPayment(payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.payments.list(),
        QueryKeyFactory.module.cx.payments.summary(remoteUtilityId),
        QueryKeyFactory.module.cx.payments.latestBill(
          remoteUtilityId,
          consumer,
          consumerNo,
        ),
      ],
      onSuccess: () => {
        toast({ title: "Payment Created" });
      },
    },
  );
};

export const useUpdatePayment = (
  paymentId: number,
  remoteUtilityId: number,
) => {
  return useSmartMutation(
    (payload: any) => paymentsApi.updatePayment(paymentId, payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.payments.details(paymentId),
        QueryKeyFactory.module.cx.payments.list(),
        QueryKeyFactory.module.cx.payments.summary(remoteUtilityId),
      ],
      onSuccess: () => {
        toast({ title: "Payment updated successfully!" });
      },
      onError: (error: any) => {
        toast({
          title: `Failed to update payment: ${error.message}`,
          variant: "destructive",
        });
      },
    },
  );
};

export const useDeletePayment = (remoteUtilityId: number) => {
  return useSmartMutation(
    (paymentId: number) => paymentsApi.deletePayment(paymentId),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.payments.list(),
        QueryKeyFactory.module.cx.payments.summary(remoteUtilityId),
      ],
      onSuccess: () => {
        toast({ title: "Payment deleted successfully!" });
      },
    },
  );
};

// ================================
// CREDIT NOTE HOOKS
// ================================

export const useCreditNotes = (filters: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.creditNotes(filters),
    () => paymentsApi.getCreditNotes(filters),
    {
      enabled: !!filters?.remoteUtilityId,
      staleTime: 2 * 60 * 1000,
    },
  );
};

export const useCreditNoteExport = (filters: any) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportQuery = useSmartQuery(
    QueryKeyFactory.module.cx.payments.creditNotes(filters),
    async () => {
      setIsExporting(true);
      try {
        const res = await paymentsApi.exportCreditNotesList(filters);
        toast({
          title: "Export Initiated",
          description: res?.message,
        });
        return res;
      } finally {
        setIsExporting(false);
      }
    },
    { enabled: false },
  );

  return {
    exportCSV: () => exportQuery.refetch(),
    isExporting: isExporting || exportQuery.isFetching,
    error: exportQuery.error,
    isError: exportQuery.isError,
  };
};

export const useCreateCreditNote = (remoteUtilityId: number) => {
  return useSmartMutation(
    (payload: any) => paymentsApi.createCreditNote(payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.payments.creditNotes(),
        QueryKeyFactory.module.cx.payments.creditNoteSummary(remoteUtilityId),
        QueryKeyFactory.module.cx.payments.list(),
      ],
      onSuccess: () => {
        toast({ title: "Credit note created successfully!" });
      },
    },
  );
};

// ================================
// CHOICE HOOKS
// ================================

export const useChoices = (choice: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.choices(choice),
    () => paymentsApi.getChoices(choice),
    {
      enabled: !!choice,
    },
  );
};

export const useCreditNoteReasons = (remoteUtilityId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.creditNoteReasons(remoteUtilityId),
    () => paymentsApi.getCreditNoteReasons(),
    {
      enabled: !!remoteUtilityId,
    },
  );
};

export const usePaymentChannelChoices = (remoteUtilityId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.paymentChannelChoices(remoteUtilityId),
    () => paymentsApi.getPaymentChannelChoices(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    },
  );
};

export const usePaymentPayTypeChoices = (remoteUtilityId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.paymentPayTypeChoices(remoteUtilityId),
    () => paymentsApi.getPaymentPayTypeChoices(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    },
  );
};

export const usePaymentReceivedStatusChoices = (remoteUtilityId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.paymentReceivedStatusChoices(
      remoteUtilityId,
    ),
    () => paymentsApi.getPaymentReceivedStatusChoices(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    },
  );
};

export const useInstallments = (consumerId: number, consumerNo: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.installments({
      remoteUtilityId: Number(remoteUtilityId),
      consumerId,
      consumerNo,
    }),
    () =>
      paymentsApi.getInstallments(
        Number(remoteUtilityId),
        consumerId,
        consumerNo,
      ),
    {
      enabled: !!consumerId && !!consumerNo,
    },
  );
};

export const usePaymentMode = (remoteUtilityId) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.paymentMethod({
      remoteUtilityId: Number(remoteUtilityId),
    }),
    () => paymentsApi.getPaymentMode(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    },
  );
};

export const useBulkPaymentBatches = (filters: {
  remoteUtilityId: number;
  page?: number;
  pageSize?: number;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.bulkPaymentBatches(filters),
    () => paymentsApi.getBulkPaymentBatches(filters),
    {
      enabled: !!filters?.remoteUtilityId,
    },
  );
};

export const useUploadBulkPaymentBatch = () => {
  return useSmartMutation(
    (payload: FormData) => paymentsApi.uploadBulkPaymentBatch(payload),
    {
      invalidateKeys: [QueryKeyFactory.module.cx.payments.bulkPaymentBatches()],
      onSuccess: () => {
        toast({ title: "Batch uploaded successfully!" });
      },
    },
  );
};

export const useBulkPaymentBatchPreview = (id: number | null) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.bulkPaymentBatchPreview(id as number),
    () => paymentsApi.getBulkPaymentBatchPreview(id as number),
    {
      enabled: !!id,
    },
  );
};

export const useSaveBulkPaymentBatchMappings = (id: number | null) => {
  return useSmartMutation(
    (payload: { column_mappings: Record<string, string> }) =>
      paymentsApi.saveBulkPaymentBatchMappings(id as number, payload),
    {
      invalidateKeys: [QueryKeyFactory.module.cx.payments.bulkPaymentBatches()],
      onSuccess: () => {
        toast({ title: "Mappings saved successfully!" });
      },
    },
  );
};

export const useBulkPaymentBatchDetails = (id: number | null) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.bulkPaymentBatchDetails(id as number),
    () => paymentsApi.getBulkPaymentBatchDetails(id as number),
    {
      enabled: !!id,
    },
  );
};

export const useBulkPaymentBatchRecords = (filters: {
  id: number;
  status?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.bulkPaymentBatchRecords(filters),
    () => paymentsApi.getBulkPaymentBatchRecords(filters),
    {
      enabled: !!filters.id,
    },
  );
};

export const usePostMatchedBulkPaymentBatch = (id: number | null) => {
  return useSmartMutation(
    () => paymentsApi.postMatchedBulkPaymentBatch(id as number),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.payments.bulkPaymentBatchDetails(
          id as number,
        ),
        QueryKeyFactory.module.cx.payments.bulkPaymentBatches(),
      ],
    },
  );
};

export const useExportExceptionsBulkPaymentBatch = (id: number | null) => {
  return useSmartMutation(() =>
    paymentsApi.exportExceptionsBulkPaymentBatch(id as number),
  );
};

export const useSearchBulkPaymentConsumers = (filters: {
  search: string;
  remoteUtilityId: number;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.consumerSearch(filters),
    () => paymentsApi.searchBulkPaymentConsumers(filters),
    {
      enabled: !!filters.remoteUtilityId && filters.search.length >= 2,
    },
  );
};
export const useSearchBulkPaymentConsumersForBillData = (filters: {
  search: string;
  remoteUtilityId: number;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.consumerSearchForBillData(filters),
    () => paymentsApi.searchBulkPaymentConsumersForBillData(filters),
    {
      enabled: !!filters.remoteUtilityId && filters.search.length >= 2,
    },
  );
};

export const useResolveBulkPaymentRecord = () => {
  return useSmartMutation(
    (params: { id: number; consumerId: number }) =>
      paymentsApi.resolveBulkPaymentRecord(params.id, {
        consumer_id: params.consumerId,
      }),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.payments.bulkPaymentBatches(),
        ["cx", "payment"], // Invalidate all payment related queries to refresh records and details
      ],
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Record resolved successfully.",
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "payment"],
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description:
            error?.response?.data?.message || "Failed to resolve record.",
          variant: "destructive",
        });
      },
    },
  );
};

export const usePaymentMethod = (remoteUtilityId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.paymentMode({
      remoteUtilityId: Number(remoteUtilityId),
    }),
    () =>
      paymentsApi.getPaymentMethod({
        remoteUtilityId: Number(remoteUtilityId),
      }),
    {
      enabled: !!remoteUtilityId,
    },
  );
};

export const useDayEndReconciliations = (filters: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.reconciliations(filters),
    () => paymentsApi.getDayEndReconciliations(filters),
    {
      enabled: !!filters?.remoteUtilityId,
    },
  );
};

export const useStartReconciliation = () => {
  return useSmartMutation(
    (payload: any) => paymentsApi.startReconciliation(payload),
    {
      onSuccess: () => {
        toast({ title: "Reconciliation started successfully!" });
        queryClient.refetchQueries({
          queryKey: ["cx", "payment", "reconciliations"],
        });
      },
      onError: (error: any) => {
        toast({
          title: "Failed to start reconciliation",
          description: error?.response?.data?.message || "An error occurred",
          variant: "destructive",
        });
      },
    },
  );
};

export const useSlipLots = (filters: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.slipLots(filters),
    () => paymentsApi.getSlipLots(filters),
    {
      enabled: !!filters?.remoteUtilityId,
    },
  );
};

export const useCreateSlipLot = () => {
  return useSmartMutation(
    (payload: any) => paymentsApi.createSlipLot(payload),
    {
      onSuccess: () => {
        toast({ title: "Slip lot created successfully!" });
        queryClient.refetchQueries({
          queryKey: ["cx", "payment", "slipLots"],
        });
      },
    },
  );
};

export const useSlipLotById = (id: number | string) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.slipLotDetails(id),
    () => paymentsApi.getSlipLotById(id),
    {
      enabled: !!id,
    },
  );
};

export const useAddSlipLotEntry = (lotId: number | string) => {
  return useSmartMutation(
    (payload: any) => paymentsApi.addSlipLotEntry(lotId, payload),
    {
      onSuccess: () => {
        toast({ title: "Entry added successfully!" });
        queryClient.refetchQueries({
          queryKey: ["cx", "payment", "slipLotEntries"],
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "payment", "slipLotDetails"],
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.response?.data?.message || "Failed to add entry.",
          variant: "destructive",
        });
      },
    },
  );
};

export const useUpdateSlipLotEntry = (lotId: number | string) => {
  return useSmartMutation(
    ({ entryId, payload }: { entryId: number | string; payload: any }) =>
      paymentsApi.updateSlipLotEntry(entryId, payload),
    {
      onSuccess: () => {
        toast({ title: "Entry updated successfully!" });
        queryClient.refetchQueries({
          queryKey: ["cx", "payment", "slipLotEntries"],
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "payment", "slipLotDetails"],
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description:
            error?.response?.data?.message || "Failed to update entry.",
          variant: "destructive",
        });
      },
    },
  );
};

export const useSlipLotEntries = (filters: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.slipLotEntries(filters),
    () => paymentsApi.getSlipLotEntries(filters),
    {
      enabled: !!filters.lotId,
    },
  );
};

export const useDeleteSlipLotEntry = () => {
  return useSmartMutation(
    (entryId: number | string) => paymentsApi.deleteSlipLotEntry(entryId),
    {
      onSuccess: () => {
        toast({ title: "Entry deleted successfully!" });
        queryClient.refetchQueries({
          queryKey: ["cx", "payment", "slipLotEntries"],
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "payment", "slipLotDetails"],
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description:
            error?.response?.data?.message || "Failed to delete entry.",
          variant: "destructive",
        });
      },
    },
  );
};

export const useSubmitSlipLot = () => {
  return useSmartMutation(
    (lotId: number | string) => paymentsApi.submitSlipLot(lotId),
    {
      onSuccess: () => {
        toast({ title: "Lot submitted successfully!" });
        queryClient.refetchQueries({
          queryKey: ["cx", "payment", "slipLots"],
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "payment", "slipLotDetails"],
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description:
            error?.response?.data?.message || "Failed to submit lot.",
          variant: "destructive",
        });
      },
    },
  );
};

export const useScanSlipLot = (lotId: number | string) => {
  return useSmartMutation(
    (payload: { payload: string }) => paymentsApi.scanSlipLot(lotId, payload),
    {
      onSuccess: () => {
        // toast will be handled in the component
        queryClient.refetchQueries({
          queryKey: ["cx", "payment", "slipLotEntries"],
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "payment", "slipLotDetails"],
        });
      },
      onSettled: () => {
        queryClient.refetchQueries({
          queryKey: ["cx", "payment", "slipLotRecentScans"],
        });
      },
    },
  );
};

export const useSlipLotRecentScans = (lotId: number | string) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.slipLotRecentScans(lotId),
    () => paymentsApi.getSlipLotRecentScans(lotId),
    {
      enabled: !!lotId,
    },
  );
};

export const useExportSlipLotErrors = (lotId: number | string) => {
  return useSmartMutation(() => paymentsApi.exportSlipLotErrors(lotId));
};

export const useOutstandingBills = (filters: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.outstanding.list(filters),
    () => paymentsApi.getOutstandingBills(filters),
    {
      enabled:
        !!filters?.remoteUtilityId &&
        (filters?.searchData ? filters.searchData.length >= 2 : true),
      staleTime: 2 * 60 * 1000,
      retry: false,
    },
  );
};

export const useReconciliationSummary = (id: number | string) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.reconciliationSummary(id),
    () => paymentsApi.getReconciliationSummary(id),
    {
      enabled: !!id,
    },
  );
};

export const useReconciliationDrillDown = (
  id: number | string,
  channelId: number | string,
  page?: number,
  limit?: number,
  search?: string,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.reconciliationDrillDown(
      id,
      channelId,
      page,
      limit,
      search,
    ),
    () =>
      paymentsApi.getReconciliationDrillDown(
        id,
        channelId,
        page,
        limit,
        search,
      ),
    {
      enabled: !!id && !!channelId,
    },
  );
};

export const useReconciliationDrillDownExport = (
  id: number | string,
  channelId: number | string,
) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportQuery = useSmartQuery(
    QueryKeyFactory.module.cx.payments.reconciliationDrillDown(
      id,
      channelId,
      undefined,
      undefined,
      "export",
    ),
    async () => {
      setIsExporting(true);
      try {
        const blob = await paymentsApi.exportReconciliationDrillDown(
          id,
          channelId,
        );
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `reconciliation_drilldown_${id}_channel_${channelId}_${new Date().getTime()}.csv`,
        );
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        toast({
          title: "Export Initiated",
          description:
            "Reconciliation drill-down list has been successfully exported.",
        });
        return blob;
      } catch (error: any) {
        toast({
          title: `Failed to export reconciliation drill-down: ${error.message}`,
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsExporting(false);
      }
    },
    { enabled: false },
  );

  return {
    exportCSV: () => exportQuery.refetch(),
    isExporting: isExporting || exportQuery.isFetching,
    error: exportQuery.error,
    isError: exportQuery.isError,
  };
};

export const useReconciliationExport = (id: number | string) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportQuery = useSmartQuery(
    QueryKeyFactory.module.cx.payments.reconciliationExport(id),
    async () => {
      setIsExporting(true);
      try {
        const blob = await paymentsApi.exportReconciliation(id);
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `reconciliation_${id}_${new Date().getTime()}.csv`,
        );
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        toast({
          title: "Export Initiated",
          description: "Reconciliation has been successfully exported.",
        });
        return blob;
      } catch (error: any) {
        toast({
          title: `Failed to export reconciliation: ${error.message}`,
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsExporting(false);
      }
    },
    { enabled: false },
  );

  return {
    exportCSV: () => exportQuery.refetch(),
    isExporting: isExporting || exportQuery.isFetching,
    error: exportQuery.error,
    isError: exportQuery.isError,
  };
};

export const useReconciliationById = (
  id: number | string,
  remoteUtilityId: number | string,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.reconciliationById(id, remoteUtilityId),
    () => paymentsApi.getReconciliationById(id, remoteUtilityId),
    {
      enabled: !!id && !!remoteUtilityId,
    },
  );
};

export const useReconciliationSystemReceipts = (filters: {
  remoteUtilityId: number | string;
  periodFrom?: string;
  periodTo?: string;
  page?: number;
  limit?: number;
  search?: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.reconciliationSystemReceipts(filters),
    () => paymentsApi.getReconciliationSystemReceipts(filters),
    {
      enabled:
        !!filters.remoteUtilityId && !!filters.periodFrom && !!filters.periodTo,
    },
  );
};

export const useActivityLogs = (filters: {
  remoteUtilityId: number | string;
  objectType?: string;
  module?: string;
  limit?: number;
  requestId?: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.activityLogs(filters),
    () => paymentsApi.getActivityLogs(filters),
    {
      enabled: !!filters.remoteUtilityId,
    },
  );
};

export const useSignOffReconciliation = (
  id: number | string,
  remoteUtilityId: number | string,
) => {
  return useSmartMutation(
    (payload: {
      manager_name: string;
      sign_off_method: number;
      remarks: string;
    }) => paymentsApi.signOffReconciliation(id, payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.payments.reconciliations(),
        QueryKeyFactory.module.cx.payments.reconciliationById(
          id,
          remoteUtilityId,
        ),
        QueryKeyFactory.module.cx.payments.reconciliationSummary(id),
      ],
      onSuccess: () => {
        toast({ title: "Day signed off successfully!" });
        queryClient.refetchQueries({
          queryKey: ["cx", "payment", "reconciliations"],
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "payment", "reconciliationById"],
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "payment", "reconciliationSummary"],
        });
      },
      onError: (error: any) => {
        toast({
          title: "Failed to sign off",
          description: error?.response?.data?.message || "An error occurred",
          variant: "destructive",
        });
      },
    },
  );
};

export const useUploadBankStatement = () => {
  return useSmartMutation(
    (params: {
      file: File;
      remoteUtilityId: number | string;
      bankAccountId: number | string;
      onUploadProgress?: (progressEvent: any) => void;
    }) => paymentsApi.uploadReconciliationStatement(params),
    {
      onSuccess: () => {
        toast({ title: "Statement uploaded successfully!" });
      },
      onError: (error: any) => {
        toast({
          title: "Failed to upload statement",
          description: error?.response?.data?.message || "An error occurred",
          variant: "destructive",
        });
      },
    },
  );
};

export const useCheckTransferPayment = (
  paymentId: number,
  remoteUtilityId: number,
  enabled: boolean = true,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.checkTransferPayment(
      paymentId,
      remoteUtilityId,
    ),
    () => paymentsApi.checkTransferPayment(paymentId, remoteUtilityId),
    {
      enabled: enabled && !!paymentId && !!remoteUtilityId,
    },
  );
};

export const useTransferConsumerSearch = (filters: {
  remoteUtilityId: number;
  search: string;
  excludeConsumerId: number;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.transferConsumerSearch(filters),
    () => paymentsApi.transferConsumerSearch(filters),
    {
      enabled: filters?.search?.length >= 2 && !!filters.remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      retry: false,
    },
  );
};

export const useTransferPayment = () => {
  return useSmartMutation(
    ({
      paymentId,
      payload,
    }: {
      paymentId: number;
      payload: {
        destination_consumer_id: number;
        remote_utility_id: number;
        transfer_reason?: string;
      };
    }) => paymentsApi.transferPayment(paymentId, payload),
    {
      onSuccess: () => {
        toast({
          title: "Payment transferred successfully",
          variant: "default",
        });
        queryClient.invalidateQueries({ queryKey: ["cx", "payment"] });
        queryClient.invalidateQueries({ queryKey: ["cx", "creditNote"] });
      },
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title:
            error?.response?.data?.message || "Failed to transfer payment.",
        });
      },
    },
  );
};

export const usePaymentChoices = (choiceType: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.paymentChoices(choiceType),
    () => paymentsApi.getPaymentChoices(choiceType),
  );
};

export const useCheckRefundPayment = (
  paymentId: number,
  remoteUtilityId: number,
  enabled: boolean = true,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.checkRefundPayment(
      paymentId,
      remoteUtilityId,
    ),
    () => paymentsApi.checkRefundPayment(paymentId, remoteUtilityId),
    { enabled: enabled && !!paymentId && !!remoteUtilityId },
  );
};

export const useReviewRefundPayment = () => {
  return useSmartMutation(
    ({ paymentId, payload }: { paymentId: number; payload: any }) =>
      paymentsApi.reviewRefundPayment(paymentId, payload),
    {
      onSuccess: () => {
        toast({
          title: "Refund request processed successfully",
          variant: "default",
        });
        queryClient.invalidateQueries({ queryKey: ["cx", "payment", "list"] });
        queryClient.invalidateQueries({
          queryKey: ["cx", "payment", "details"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cx", "payment", "summary"],
        });
        queryClient.invalidateQueries({ queryKey: ["cx", "creditNote"] });
      },
      onError: (err: any) => {
        toast({
          variant: "destructive",
          title: "Failed to process refund",
          description:
            err?.response?.data?.message ||
            err?.message ||
            "Something went wrong",
        });
      },
    },
  );
};

export const useDownloadBulkPaymentTemplate = () => {
  return useSmartMutation(
    () => paymentsApi.downloadBulkPaymentCsvTemplate(),
    {
      onSuccess: (data) => {
        downloadCSV(data, "bulk_payment_template");
      },
      onError: (err: any) => {
        toast({
          variant: "destructive",
          title: "Download Failed",
          description:
            err?.response?.data?.message ||
            err?.message ||
            "Failed to download template",
        });
      },
    },
  );
};
